/** JSON Schema walking helpers shared by the renderer. */

import type { JsonSchema, UiHints } from '@/core/types/api.types'

export function hints(schema: JsonSchema): UiHints {
  return schema['x-ui'] ?? {}
}

export function labelFor(key: string, schema: JsonSchema): string {
  const explicit = hints(schema).label ?? schema.title
  if (explicit) return explicit
  // `user_id` -> `User id`. Better than showing a raw key, and manifests can
  // always override with x-ui.label.
  return key.replace(/[_-]+/g, ' ').replace(/^\w/, (c) => c.toUpperCase())
}

export function schemaType(schema: JsonSchema): string {
  const t = schema.type
  if (Array.isArray(t)) return t.find((x) => x !== 'null') ?? 'string'
  return t ?? 'string'
}

/** Property keys in the order the manifest asked for, then alphabetically. */
export function orderedKeys(schema: JsonSchema): string[] {
  const props = schema.properties ?? {}
  return Object.keys(props).sort((a, b) => {
    const oa = hints(props[a]).order
    const ob = hints(props[b]).order
    if (oa != null && ob != null) return oa - ob
    if (oa != null) return -1
    if (ob != null) return 1
    return a.localeCompare(b)
  })
}

export function isRequired(schema: JsonSchema, key: string): boolean {
  return (schema.required ?? []).includes(key)
}

/** Seed form state from the schema's declared defaults. */
export function defaultsFor(schema: JsonSchema): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const [key, prop] of Object.entries(schema.properties ?? {})) {
    if (prop.default !== undefined) {
      out[key] = prop.default
    } else if (schemaType(prop) === 'boolean') {
      // An unchecked box must submit `false`, not absent — otherwise a boolean
      // with no default silently never sends.
      out[key] = false
    }
  }
  return out
}

/**
 * Strip values the user left empty so the backend omits those keys entirely.
 *
 * Mirrors the engine's rule: an absent optional field disappears from the
 * request rather than being sent as null, which providers reject far more often.
 */
export function pruneEmpty(values: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(values)) {
    if (value === undefined || value === null) continue
    if (typeof value === 'string' && value.trim() === '') continue
    if (Array.isArray(value) && value.length === 0) continue
    if (
      typeof value === 'object' &&
      !Array.isArray(value) &&
      Object.keys(value as object).length === 0
    ) {
      continue
    }
    out[key] = value
  }
  return out
}

export function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}
